import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Building2, Mail, Phone, MapPin, Calendar, Copy, Trash2, Pencil } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCompanies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    contract_days: 1,
    contract_type: 'mensual'
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    contract_days: 1,
    contract_type: 'mensual'
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data);
    } catch (error) {
      toast.error('Error al cargar empresas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.contact_email) {
      toast.error('Nombre y email son requeridos');
      return;
    }

    try {
      await axios.post(`${API}/companies`, formData);
      toast.success('Empresa creada correctamente');
      setDialogOpen(false);
      setFormData({ name: '', contact_email: '', contact_phone: '', address: '', contract_days: 1, contract_type: 'mensual' });
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear empresa');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro? Se eliminarán todos los datos asociados.')) return;
    
    try {
      await axios.delete(`${API}/companies/${id}`);
      toast.success('Empresa eliminada');
      fetchCompanies();
    } catch (error) {
      toast.error('Error al eliminar empresa');
    }
  };

  const copyInvitationCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Código copiado al portapapeles');
  };

  const openEditDialog = (company) => {
    setEditingCompany(company);
    setEditFormData({
      name: company.name,
      contact_email: company.contact_email,
      contact_phone: company.contact_phone || '',
      address: company.address || '',
      contract_days: company.contract_days,
      contract_type: company.contract_type
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editFormData.name || !editFormData.contact_email) {
      toast.error('Nombre y email son requeridos');
      return;
    }

    try {
      await axios.put(`${API}/companies/${editingCompany.id}`, editFormData);
      toast.success('Empresa actualizada correctamente');
      setEditDialogOpen(false);
      setEditingCompany(null);
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar empresa');
    }
  };

  return (
    <Layout title="Gestión de Empresas">
      <div className="space-y-6 animate-fade-in" data-testid="admin-companies">
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-slate-400">Administra las empresas clientes y sus contratos</p>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-sky-600 hover:bg-sky-500 text-white gap-2" data-testid="add-company-btn">
                <Plus className="w-4 h-4" />
                Nueva Empresa
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-rajdhani text-xl">Nueva Empresa</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Nombre de la Empresa *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="Industrias ABC S.L."
                    data-testid="company-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Email de Contacto *</Label>
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="contacto@empresa.com"
                    data-testid="company-email-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Teléfono</Label>
                    <Input
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white"
                      placeholder="+34 600 000 000"
                      data-testid="company-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Días/Semana</Label>
                    <Select
                      value={String(formData.contract_days)}
                      onValueChange={(v) => setFormData({ ...formData, contract_days: parseInt(v) })}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="company-days-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="1">1 día</SelectItem>
                        <SelectItem value="2">2 días</SelectItem>
                        <SelectItem value="3">3 días</SelectItem>
                        <SelectItem value="4">4 días</SelectItem>
                        <SelectItem value="5">5 días</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Dirección</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="Calle Industrial, 123"
                    data-testid="company-address-input"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-400">
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-sky-600 hover:bg-sky-500" data-testid="save-company-btn">
                    Crear Empresa
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Companies Grid */}
        {loading ? (
          <div className="text-center py-12 text-slate-500">Cargando...</div>
        ) : companies.length === 0 ? (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="py-12 text-center">
              <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No hay empresas registradas</p>
              <p className="text-sm text-slate-500 mt-1">Crea la primera empresa para comenzar</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {companies.map((company) => (
              <Card key={company.id} className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-all">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <CardTitle className="font-rajdhani text-lg text-white">{company.name}</CardTitle>
                    <div className="flex gap-1 -mt-1 -mr-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(company)}
                        className="text-slate-500 hover:text-sky-400 hover:bg-sky-500/10"
                        data-testid={`edit-company-${company.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(company.id)}
                        className="text-slate-500 hover:text-rose-400 hover:bg-rose-500/10"
                        data-testid={`delete-company-${company.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <Mail className="w-4 h-4 text-slate-500" />
                    <span className="truncate">{company.contact_email}</span>
                  </div>
                  {company.contact_phone && (
                    <div className="flex items-center gap-2 text-sm text-slate-400">
                      <Phone className="w-4 h-4 text-slate-500" />
                      <span>{company.contact_phone}</span>
                    </div>
                  )}
                  {company.address && (
                    <div className="flex items-center gap-2 text-sm text-slate-400">
                      <MapPin className="w-4 h-4 text-slate-500" />
                      <span className="truncate">{company.address}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <Calendar className="w-4 h-4 text-slate-500" />
                    <span>{company.contract_days} día(s)/semana</span>
                  </div>
                  
                  {/* Invitation Code */}
                  <div className="pt-3 mt-3 border-t border-slate-800">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Código de Invitación</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-slate-800 text-sky-400 px-3 py-2 rounded text-sm font-mono tracking-widest">
                        {company.invitation_code}
                      </code>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => copyInvitationCode(company.invitation_code)}
                        className="text-slate-400 hover:text-sky-400"
                        data-testid={`copy-code-${company.id}`}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-rajdhani text-xl">Editar Empresa</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Nombre de la Empresa *</Label>
                <Input
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                  placeholder="Industrias ABC S.L."
                  data-testid="edit-company-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Email de Contacto *</Label>
                <Input
                  type="email"
                  value={editFormData.contact_email}
                  onChange={(e) => setEditFormData({ ...editFormData, contact_email: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                  placeholder="contacto@empresa.com"
                  data-testid="edit-company-email-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Teléfono</Label>
                  <Input
                    value={editFormData.contact_phone}
                    onChange={(e) => setEditFormData({ ...editFormData, contact_phone: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="+34 600 000 000"
                    data-testid="edit-company-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Días/Semana</Label>
                  <Select
                    value={String(editFormData.contract_days)}
                    onValueChange={(v) => setEditFormData({ ...editFormData, contract_days: parseInt(v) })}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="edit-company-days-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="1">1 día</SelectItem>
                      <SelectItem value="2">2 días</SelectItem>
                      <SelectItem value="3">3 días</SelectItem>
                      <SelectItem value="4">4 días</SelectItem>
                      <SelectItem value="5">5 días</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Dirección</Label>
                <Input
                  value={editFormData.address}
                  onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                  placeholder="Calle Industrial, 123"
                  data-testid="edit-company-address-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="ghost" onClick={() => setEditDialogOpen(false)} className="text-slate-400">
                  Cancelar
                </Button>
                <Button type="submit" className="bg-sky-600 hover:bg-sky-500" data-testid="save-edit-company-btn">
                  Guardar Cambios
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
